import { useMemo } from 'react';

import { useDebounce } from '../../../hooks';
import { DEBOUNCE_TIME, DEFAULT_PAGE_SIZE } from '../constants';

import { usePagination } from './usePagination';
import { useSearchQueryChange } from './useSearchQueryChange';

/**
 * GraphQL query input type for searching orders
 */
export type SearchOrderInput = {
  keywords?: string;
  supplierRefId?: string;
};

/**
 * GraphQL query variables for GetOrders query
 */
export type GetOrdersQueryVariables = {
  first?: number;
  after?: string;
  before?: string;
  query: SearchOrderInput;
};

export type UseQueryVariablesReturn = {
  /** Formatted GraphQL query variables */
  variables: GetOrdersQueryVariables;
  /** Pagination state and handlers */
  pagination: ReturnType<typeof usePagination>;
  /** Search state and handlers */
  search: ReturnType<typeof useSearchQueryChange>;
};

/**
 * Builds GraphQL query variables from search and pagination state
 *
 * @param searchTerm - Keywords to search for
 * @param supplierRefId - Supplier reference ID to filter by
 * @param pageSize - Number of results per page
 * @param cursorAfter - Cursor for forward pagination
 * @param cursorBefore - Cursor for backward pagination
 * @returns Formatted query variables for GetOrders query
 */
export const getQueryVariables = ({
  searchTerm = '',
  supplierRefId = '',
  pageSize = DEFAULT_PAGE_SIZE,
  cursorBefore,
  cursorAfter,
}: {
  searchTerm?: string;
  supplierRefId?: string;
  pageSize?: number;
  cursorBefore?: string;
  cursorAfter?: string;
} = {}): GetOrdersQueryVariables => {
  return {
    query: {
      keywords: searchTerm.length > 0 ? searchTerm : undefined,
      supplierRefId: supplierRefId.length > 0 ? supplierRefId : undefined,
    },
    after: cursorAfter,
    before: cursorBefore,
    first: pageSize,
  };
};

/**
 * Hook that combines pagination and search state into GraphQL query variables
 *
 * This hook orchestrates the usePagination and useSearchQueryChange hooks,
 * combining their state into properly formatted GraphQL query variables for
 * the GetOrders query.
 *
 * @returns Query variables and state from pagination and search hooks
 *
 * @example
 * ```tsx
 * const { variables, pagination, search } = useQueryVariables();
 *
 * const { data, loading, error } = useGetOrdersQuery({
 *   variables,
 *   fetchPolicy: 'cache-and-network',
 * });
 *
 * // Access search state
 * const { keywordsInputValue, onKeywordsInputChange } = search;
 *
 * // Access pagination state
 * const { pageSize, hasNextPage } = pagination;
 * ```
 */
export const useQueryVariables = (): UseQueryVariablesReturn => {
  const pagination = usePagination();
  const { cursorBefore, cursorAfter, pageSize } = pagination;

  const search = useSearchQueryChange({
    cursorBefore,
    cursorAfter,
    pageSize,
  });

  const { keywordsNormalized, supplierRefIdNormalized } = search;

  // Debounce the search terms so the query only fires once the user pauses
  // typing, rather than on every keystroke.
  const debouncedKeywords = useDebounce(keywordsNormalized, DEBOUNCE_TIME);
  const debouncedSupplierRefId = useDebounce(
    supplierRefIdNormalized,
    DEBOUNCE_TIME
  );

  // Build GraphQL query variables
  const variables = useMemo(
    () =>
      getQueryVariables({
        searchTerm: debouncedKeywords,
        supplierRefId: debouncedSupplierRefId,
        cursorBefore,
        cursorAfter,
        pageSize,
      }),
    [
      debouncedKeywords,
      debouncedSupplierRefId,
      cursorBefore,
      cursorAfter,
      pageSize,
    ]
  );

  return useMemo(
    () => ({
      variables,
      pagination,
      search,
    }),
    [variables, pagination, search]
  );
};
